import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
} from '@chakra-ui/form-control'
import { Input } from '@chakra-ui/input'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/modal'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Select,
  Text,
} from '@chakra-ui/react'
import type { FieldProps } from 'formik'
import { Field, Formik } from 'formik'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import * as Yup from 'yup'
import useToast from '../../hooks/useToast'
import type {
  DeveloperResponse,
  RollKeyErrorResponse,
  RollKeySuccessResponse,
  UserSessionResponse,
} from '../../interfaces'
import { UserContext } from '../../providers'
import PasswordInput from '../PasswordInput'
import SetPasswordAlert from '../SetPasswordAlert'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  selectedKeyId: string | null
  request: Req<DeveloperResponse>
  onClose(): void
  setSelectedKeyId(string?): void
  getData(): Promise<void>
}

interface FormProps {
  timePeriod: string
  email: string
  password: string
}

const RollApiKeyModal = ({
  isOpen,
  onClose,
  user,
  selectedKeyId,
  setSelectedKeyId,
  getData,
  request,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { refetch } = useContext(UserContext)
  const toast = useToast()
  const [formError, setFormError] = useState<string>('')

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{
            timePeriod: '',
            email: user?.userEmail ?? '',
            password: '',
          }}
          onSubmit={async (values): Promise<void> => {
            setFormError('')
            if (user?.clientNumber) {
              const response = (await request.patch(
                `/api/clients/${user.clientNumber}/settings/developer/keys/${selectedKeyId}`,
                {
                  userEmail: values.email,
                  userPassword: values.password,
                  timePeriod: values.timePeriod,
                },
              )) as unknown as RollKeySuccessResponse | RollKeyErrorResponse

              if (request.error) {
                setFormError(
                  (response as RollKeyErrorResponse)?.message ??
                    t(
                      'invalid_account_credentials_entered',
                      'You have entered invalid account credentials.',
                    ),
                )
              } else {
                toast({
                  description: t(
                    'successfully_rolled_api_key',
                    'Your api keys have been successfully rolled',
                  ),
                  status: 'success',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
                onClose()
                setSelectedKeyId(null)
                await getData()
                await refetch()
              }
            }
          }}
          validationSchema={Yup.object().shape({
            timePeriod: Yup.string().required('Required'),
            email: Yup.string().required('Required'),
            password: Yup.string().required('Required'),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty }) => (
            <>
              <ModalHeader>{t('roll_api_keys', 'Roll API keys')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {!user?.isPasswordSetByUser ? <SetPasswordAlert /> : null}
                {formError ? (
                  <Alert status="error" mb="1em">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                  </Alert>
                ) : null}
                <Text>{t('roll_api_keys_description')}</Text>
                <Field name="timePeriod">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      mt="1em"
                      isInvalid={Boolean(
                        form.errors.timePeriod && form.touched.timePeriod,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t(
                          'old_key_expiration_time',
                          'Old key Expiration Time',
                        )}
                      </FormLabel>
                      <Select {...field}>
                        <option value="">
                          {t('select_time_period', 'Select time period')}
                        </option>
                        <option value="0">
                          {t('immediately', 'Immediately')}
                        </option>
                        <option value="3600">
                          {t('in_1_hour', 'In 1 hour')}
                        </option>
                        <option value="86400">
                          {t('in_24_hours', 'In 24 hours')}
                        </option>
                        <option value="259200">
                          {t('in_3_days', 'In 3 days')}
                        </option>
                        <option value="604800">
                          {t('in_7_days', 'In 7 days')}
                        </option>
                        <option value="7776000">
                          {t('in_90_days', 'In 90 days')}
                        </option>
                      </Select>
                      <FormErrorMessage marginBottom="-2.05em">
                        {form.errors.timePeriod}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Text mt="2em">
                  {t(
                    'roll_keys_to_continue_authenticate',
                    'To continue, please enter your account password.',
                  )}
                </Text>
                <Field name="email">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      mt="0.5em"
                      isInvalid={Boolean(
                        form.errors.email && form.touched.email,
                      )}
                      isDisabled
                      isReadOnly
                      isRequired
                    >
                      <FormLabel>{t('email_uppercase_e', 'Email')}</FormLabel>
                      <Input {...field} placeholder="Email" />
                      <FormErrorMessage marginBottom="-2.05em">
                        {form.errors.email}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Field name="password">
                  {({ field, form }: FieldProps<string, FormProps>) => (
                    <FormControl
                      mt="1em"
                      isInvalid={Boolean(
                        form.errors.password && form.touched.password,
                      )}
                      isRequired
                    >
                      <FormLabel>
                        {t('password_uppercase_p', 'Password')}
                      </FormLabel>
                      <PasswordInput
                        field={field}
                        isDisabled={!user?.isPasswordSetByUser}
                        isPasswordSetByUser={user?.isPasswordSetByUser ?? false}
                        showToggle={true}
                      />
                      <FormErrorMessage marginBottom="-2.05em">
                        {form.errors.password}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button
                  onClick={() => {
                    onClose()
                    setFormError('')
                  }}
                  variant="outline"
                >
                  {t('cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  {t('submit', 'Submit')}
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default RollApiKeyModal
